import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import Modal from 'components/modal';
import { StoreLocatorEmptyStates as EmptyState } from 'components/store-locator-empty-states';
import { useStoreLocatorContext } from 'pages/store-locator/hooks';
import { StoreCardsList } from 'pages/store-locator/old-ui/store-cards';
import { SearchBar } from 'pages/store-locator/search-bar';
import { TabPanels, TabsBar } from 'pages/store-locator/tabs';
import { useGeolocation } from 'state/geolocation';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import noop from 'utils/noop';

import { ModalBottom, ModalContent, ModalHeading, ModalTop } from '../store-selector.styled';
import { IStoreSelectorProps } from '../types';

const StoreSelector: React.FC<IStoreSelectorProps> = ({ onDismiss, updateStore }) => {
  const {
    activeStoreId,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    currentTabHasError,
    nearbyStoreCardRefs,
    storesNearby,
    storesFavs,
    storesRecent,
    currentTab,
    handleTabChange,
    locatorTabs,
    currentTabIndex,
    locatorTabIds,
    setActiveStoreId,
  } = useStoreLocatorContext();
  const {
    manualAddress,
    setManualAddress,
    isPermissionGranted,
    isPermissionChecking,
    coordinatesAvailable,
    updateManualCoordinates,
  } = useGeolocation();
  const { formatMessage } = useIntl();

  const isSearchingNearby =
    (searchingNearby && coordinatesAvailable) ||
    isPermissionChecking ||
    (isPermissionGranted && !coordinatesAvailable);
  const isSearchingRecent = searchingRecent || isPermissionChecking;
  const isSearchingFavorites = searchingFavs || isPermissionChecking;

  const handleTabChangeEvent = useCallback(
    (_event: React.ChangeEvent, newValue: number) => {
      handleTabChange(newValue);
    },
    [handleTabChange]
  );

  const onButtonClickOverride = useCallback(
    (storeId: string) => {
      onDismiss();
      setActiveStoreId(storeId);
      updateStore(storeId);
    },
    [onDismiss, setActiveStoreId, updateStore]
  );

  const noStoresElement = (
    <EmptyState
      tab={currentTab}
      locationPermissionsGranted={isPermissionGranted}
      coordinatesAvailable={coordinatesAvailable}
      currentTabError={currentTabHasError}
    />
  );

  const nearbyStoreCardsEl = (
    <StoreCardsList
      searching={isSearchingNearby}
      stores={storesNearby}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={noop}
      activeStoreId={activeStoreId}
      buttonOnly={true}
      buttonTextOverride={formatMessage({ id: 'select' })}
      onButtonClickOverride={onButtonClickOverride}
      allStoresSelectable={true}
    />
  );

  const favStoreCardsEl = (
    <StoreCardsList
      searching={isSearchingFavorites}
      stores={storesFavs}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={noop}
      activeStoreId={activeStoreId}
      buttonOnly={true}
      buttonTextOverride={formatMessage({ id: 'select' })}
      onButtonClickOverride={onButtonClickOverride}
      allStoresSelectable={true}
    />
  );

  const recentStoreCardsEl = (
    <StoreCardsList
      searching={isSearchingRecent}
      stores={storesRecent}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={noop}
      activeStoreId={activeStoreId}
      buttonOnly={true}
      buttonTextOverride={formatMessage({ id: 'select' })}
      onButtonClickOverride={onButtonClickOverride}
      allStoresSelectable={true}
    />
  );

  const cardsByTab = {
    [OrderLocatorTab.NEARBY]: nearbyStoreCardsEl,
    [OrderLocatorTab.FAVORITE]: favStoreCardsEl,
    [OrderLocatorTab.RECENT]: recentStoreCardsEl,
  };

  return (
    <Modal onDismiss={onDismiss}>
      <ModalContent data-testid="support-form-v2-store-selector-old">
        <ModalTop>
          <ModalHeading>{formatMessage({ id: 'searchLocations' })}</ModalHeading>
          <SearchBar
            autoFocus={!manualAddress && isPermissionGranted}
            onAddressSelected={updateManualCoordinates}
            onInputValueChange={setManualAddress}
            value={manualAddress}
          />
          <TabsBar
            locatorTabs={locatorTabs}
            locatorTabIds={locatorTabIds}
            currentTabIndex={currentTabIndex}
            handleTabChangeEvent={handleTabChangeEvent}
            hideDeliveryTab={true}
          />
        </ModalTop>
        <ModalBottom>
          <TabPanels
            locatorTabs={locatorTabs}
            locatorTabIds={locatorTabIds}
            currentTabIndex={currentTabIndex}
            cardsByTab={cardsByTab}
          />
        </ModalBottom>
      </ModalContent>
    </Modal>
  );
};

export default StoreSelector;
